import { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    LinearProgress,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
    ExportUnsupportedError,
    searchTraderText,
    type TraderSearchRequest,
} from '../../api/traderSearch';
import { Choice, Row } from '../../event/export/ExportBox';
import { saveBlob, type ExportProgress } from '../../event/export/exportRun';
import type { ExportFile } from '../../event/export/exportTypes';
import {
    TRADER_EXPORT_FIELDS,
    TRADER_EXPORT_LARGE,
    TRADER_EXPORT_PAGE_SIZE,
    runTraderExport,
    traderExportPages,
} from './traderExport';

export interface TraderExportBoxProps {
    request: TraderSearchRequest | null;
    total: number | null;
    counting: boolean;
    loadPage?: (
        request: TraderSearchRequest,
        signal: AbortSignal
    ) => Promise<string>;
    save?: (blob: Blob, filename: string) => void;
    retryDelayMs?: number;
    // The clock behind the time left; the tests set it.
    now?: () => number;
    // The fields of the visible columns (exportFieldsFor); without it the
    // box has no Columns choice and writes every field.
    shownFields?: readonly string[];
}

type Status =
    | { kind: 'idle' }
    | { kind: 'running'; progress: ExportProgress; leftMs: number | null }
    | { kind: 'done'; filename: string; rows: number }
    | { kind: 'error'; message: string };

const LOAD_FAILED = 'The data could not be loaded.';

function fmt(n: number): string {
    return n.toLocaleString('en-US');
}

// "about 40 s left" / "about 2 min left": a guess from the pace so far, so
// seconds are rounded to ten.
export function timeLeftText(ms: number): string {
    const seconds = Math.max(1, Math.round(ms / 1000));
    if (seconds < 55)
        return `about ${Math.max(10, Math.round(seconds / 10) * 10)} s left`;
    return `about ${Math.max(1, Math.round(seconds / 60))} min left`;
}

function estimateText(
    total: number | null,
    counting: boolean
): { text: string; blocked: boolean } {
    if (total === null && counting) {
        return {
            text: 'Counting the matching traders… The download can start once the count is back.',
            blocked: true,
        };
    }
    if (total === null) {
        return {
            text: 'The number of matching traders is not known, so the download cannot start. Try again later.',
            blocked: true,
        };
    }
    if (total === 0)
        return { text: 'No trader matches the filters.', blocked: true };
    const requests = traderExportPages(total).length;
    const size = `${fmt(total)} ${total === 1 ? 'trader' : 'traders'} in ${requests} ${requests === 1 ? 'request' : 'requests'} of up to ${fmt(TRADER_EXPORT_PAGE_SIZE)} rows.`;
    return {
        text:
            total > TRADER_EXPORT_LARGE
                ? `${size} Large download: it can take several minutes.`
                : size,
        blocked: false,
    };
}

export function TraderExportBox(props: TraderExportBoxProps) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [file, setFile] = useState<ExportFile>('csv');
    const [columns, setColumns] = useState<'shown' | 'all'>('shown');
    const [status, setStatus] = useState<Status>({ kind: 'idle' });
    const controller = useRef<AbortController | null>(null);

    // A new filter or sort makes a running download describe the old rows.
    const requestKey = JSON.stringify(props.request);
    useEffect(() => {
        controller.current?.abort();
        setStatus({ kind: 'idle' });
    }, [requestKey]);
    useEffect(() => () => controller.current?.abort(), []);

    const running = status.kind === 'running';
    const { text, blocked } = estimateText(props.total, props.counting);

    const start = async () => {
        if (!props.request || props.total === null || blocked) return;
        const abort = new AbortController();
        controller.current = abort;
        const clock = props.now ?? Date.now;
        const startedAt = clock();
        setStatus({
            kind: 'running',
            progress: { done: 0, total: traderExportPages(props.total).length },
            leftMs: null,
        });
        try {
            const result = await runTraderExport(
                props.request,
                props.total,
                file,
                {
                    loadPage: props.loadPage ?? searchTraderText,
                    signal: abort.signal,
                    retryDelayMs: props.retryDelayMs,
                    fields:
                        columns === 'shown' && props.shownFields
                            ? props.shownFields
                            : undefined,
                    onProgress: progress => {
                        if (abort.signal.aborted) return;
                        // The pace so far, per finished page, over the rest.
                        const leftMs =
                            progress.done > 0
                                ? ((clock() - startedAt) / progress.done) *
                                  (progress.total - progress.done)
                                : null;
                        setStatus({ kind: 'running', progress, leftMs });
                    },
                }
            );
            if (abort.signal.aborted) return;
            (props.save ?? saveBlob)(result.blob, result.filename);
            setStatus({
                kind: 'done',
                filename: result.filename,
                rows: result.rows,
            });
        } catch (error) {
            if (abort.signal.aborted) return;
            console.error('trader download failed:', error);
            setStatus({
                kind: 'error',
                message:
                    error instanceof ExportUnsupportedError
                        ? error.message
                        : LOAD_FAILED,
            });
        }
    };

    const cancel = () => {
        controller.current?.abort();
        setStatus({ kind: 'idle' });
    };

    const percent =
        status.kind === 'running' && status.progress.total > 0
            ? (status.progress.done / status.progress.total) * 100
            : 0;

    return (
        <>
            <Tooltip
                title="Download all matching traders as CSV or JSON"
                describeChild
            >
                <span>
                    <Button
                        size="small"
                        aria-label="Download traders"
                        disabled={!props.request}
                        onClick={e => setAnchor(e.currentTarget)}
                        startIcon={
                            <DownloadIcon
                                sx={{ fontSize: '14px !important' }}
                            />
                        }
                        sx={{
                            fontSize: '10px',
                            minWidth: 0,
                            py: 0.25,
                            px: 0.5,
                            '& .MuiButton-startIcon': { mr: 0.25 },
                        }}
                    >
                        Download
                    </Button>
                </span>
            </Tooltip>
            <Popover
                open={anchor !== null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 360, p: 2 } } }}
            >
                <Box data-testid="trader-export-box">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: 1,
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                            Download traders
                        </Typography>
                    </Box>

                    <Row label="File" hint="The format of the file.">
                        <Choice
                            label="File"
                            value={file}
                            disabled={running}
                            onChange={setFile}
                            options={[
                                {
                                    value: 'csv',
                                    label: 'CSV',
                                    hint: 'A table as text. Opens in Excel and in pandas.',
                                },
                                {
                                    value: 'json',
                                    label: 'JSON',
                                    hint: 'Structured data with the search request at the top.',
                                },
                            ]}
                        />
                    </Row>

                    {props.shownFields && (
                        <Row
                            label="Columns"
                            hint="Which fields the file holds."
                        >
                            <Choice
                                label="Columns"
                                value={columns}
                                disabled={running}
                                onChange={setColumns}
                                options={[
                                    {
                                        value: 'shown',
                                        label: `As shown (${props.shownFields.length})`,
                                        hint: 'The columns on screen, in their order, plus address, labels and main category. Numbers stay raw.',
                                    },
                                    {
                                        value: 'all',
                                        label: `All fields (${TRADER_EXPORT_FIELDS.length})`,
                                        hint: 'Every field the search returns, including those without a column.',
                                    },
                                ]}
                            />
                        </Row>
                    )}

                    <Typography
                        data-testid="trader-export-estimate"
                        sx={{
                            fontSize: '11px',
                            mt: 1,
                            color:
                                blocked && !props.counting
                                    ? 'error.main'
                                    : 'text.secondary',
                        }}
                    >
                        {text}
                    </Typography>

                    {status.kind === 'running' && (
                        <Box sx={{ mt: 1.5 }}>
                            <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    mt: 0.5,
                                }}
                            >
                                {`Loading ${status.progress.done} of ${status.progress.total}`}
                                {status.leftMs !== null &&
                                    status.progress.done <
                                        status.progress.total &&
                                    `, ${timeLeftText(status.leftMs)}`}
                            </Typography>
                        </Box>
                    )}
                    {status.kind === 'done' && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'success.main',
                                mt: 1,
                                wordBreak: 'break-all',
                            }}
                        >
                            {`Done: ${fmt(status.rows)} rows in ${status.filename}`}
                        </Typography>
                    )}
                    {status.kind === 'error' && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'error.main',
                                mt: 1,
                            }}
                        >
                            {status.message}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mt: 1.5,
                        }}
                    >
                        {running ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={cancel}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<DownloadIcon />}
                                disabled={blocked || !props.request}
                                onClick={start}
                                sx={{ textTransform: 'none' }}
                            >
                                {status.kind === 'error'
                                    ? 'Try again'
                                    : 'Download'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
